import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatabaseBackup, Download, FileArchive, Loader2, Upload } from "lucide-react";
import { SnapshotList } from "@/components/SnapshotList";

const saveBlob = (blob, name) => {
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: name });
  a.click(); URL.revokeObjectURL(a.href);
};

export function BackupPanel() {
  const ref = useRef(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [exported, setExported] = useState(null);
  const headers = { "X-Backup-Password": pw };
  const err = (e) => toast.error(e.response?.status === 401 ? "Yedekleme şifresi hatalı" : (e.response?.data?.detail || e.message));

  const download = async () => {
    if (!pw) { toast.error("Şifre girin"); return; }
    setBusy("backup");
    try {
      const r = await api.get("/admin/backup", { headers, responseType: "blob", timeout: 300000 });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "santiye-yedek.json.gz";
      saveBlob(r.data, name);
      toast.success("Yedek indirildi");
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const exportZip = async () => {
    if (!pw) { toast.error("Şifre girin"); return; }
    setBusy("export"); setExported(null);
    try {
      const r = await api.get("/admin/export.zip", { headers, responseType: "blob", timeout: 600000 });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "santiye-export.zip";
      saveBlob(r.data, name);
      setExported({ env: r.headers["x-export-environment"], name });
      toast.success("Dışa aktarım indirildi");
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    ref.current.value = "";
    if (!file) return;
    if (!window.confirm("Bu ortamdaki TÜM veriler (her iki şirket) silinip yedekteki verilerle değiştirilecek. Devam edilsin mi?")) return;
    setBusy("restore"); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/restore", fd, { headers, timeout: 600000 });
      setResult(r.data); toast.success("Yedek geri yüklendi");
    } catch (e2) { err(e2); } finally { setBusy(""); }
  };

  return (
    <div className="mt-8 rounded-xl border border-border bg-card p-6 max-w-3xl" data-testid="backup-panel">
      <div className="flex items-center gap-2 font-display font-bold text-lg"><DatabaseBackup className="w-5 h-5 text-amber-600" /> Yedekleme ve Geri Yükleme</div>
      <p className="text-sm text-muted-foreground mt-1 mb-4">SİO ve TKC'ye ait tüm veriler (birleştirmeler, kesintiler, puantaj, günlükler dahil) tek bir dosyada yedeklenir. Bu dosyayı başka bir ortamda (ör. canlı yayın) geri yükleyebilirsiniz.</p>
      <div className="flex flex-wrap gap-3 items-center">
        <Input type="password" placeholder="Yedekleme şifresi" value={pw} onChange={e => setPw(e.target.value)} className="w-56" data-testid="backup-password-input" />
        <Button variant="outline" onClick={download} disabled={!!busy} data-testid="backup-download-button">
          {busy === "backup" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Yedeği İndir
        </Button>
        <Button variant="outline" onClick={exportZip} disabled={!!busy} className="border-emerald-600/50 text-emerald-700 dark:text-emerald-400" data-testid="export-zip-button">
          {busy === "export" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileArchive className="w-4 h-4 mr-1" />} Tam Dışa Aktarım (ZIP)
        </Button>
        <input type="file" accept=".gz" ref={ref} className="hidden" onChange={restore} data-testid="restore-file-input" />
        <Button onClick={() => pw ? ref.current?.click() : toast.error("Şifre girin")} disabled={!!busy} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="restore-button">
          {busy === "restore" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Yedeği Geri Yükle
        </Button>
      </div>
      {exported && (
        <div className={`mt-3 text-sm rounded-lg border p-3 ${exported.env === "production" ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/40"}`} data-testid="export-result">
          <strong>{exported.name}</strong> indirildi · Ortam: <strong data-testid="export-environment">{exported.env === "production" ? "PRODUCTION (canlı)" : "PREVIEW (önizleme)"}</strong>
          {exported.env !== "production" && " — canlı veriyi almak için bu işlemi yayınlanmış uygulamada yapın."}
        </div>
      )}
      <SnapshotList pw={pw} onError={err} saveBlob={saveBlob} onRestored={(d) => { setResult(d); toast.success("Yedek geri yüklendi"); }} />
      {result && (
        <div className="mt-4 text-sm rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3" data-testid="restore-result">
          Geri yüklendi: {Object.entries(result.restored).filter(([, n]) => n).map(([k, n]) => `${k}: ${n}`).join(" · ")}
        </div>
      )}
    </div>
  );
}
